'use client'
import { memo, useEffect, useMemo, useCallback } from 'react'
import styled from 'styled-components'
import { AnimatePresence, motion, useAnimation, type Variants } from 'motion/react'
import { SLIDE_TIME, useSlider } from '@/hooks'
import { getTransition, mq, rgbAlpha, vw } from '@/styles'
import { Img } from '@/components'
import { TypeCmsVariant } from '@/schema'

interface ImageSliderProps {
  data: TypeCmsVariant['media']
  isPack?: boolean
}

interface TimelineProps {
  currentIdx: number
  nSlides: number
}

export const Timeline = memo<TimelineProps>(({ currentIdx, nSlides }) => {
  const controls = useAnimation()

  const spanVariants = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const variants: Record<string, any> = {}
    const percentage = 100 / nSlides

    for (let idx = 0; idx < nSlides; idx++) {
      variants[idx] = {
        width: `${(idx + 1) * percentage}%`,
        transition: { ease: 'linear', duration: SLIDE_TIME / 1000 }
      }
      variants[`${idx}prev`] = {
        width: `${idx * percentage}%`,
        transition: { ease: 'linear', duration: 0.1 }
      }
    }

    return variants
  }, [nSlides])

  const changeSpan = useCallback(async (idx: number) => {
    await controls.start(`${idx}prev`)
    controls.start(`${idx}`)
  }, [controls])

  useEffect(() => {
    changeSpan(currentIdx)
    return () => controls.stop()
  }, [currentIdx, changeSpan, controls])

  return (
    <TimelineContainer>
      <span className='span-container'>
        <motion.span variants={spanVariants} animate={controls} />
      </span>
    </TimelineContainer>
  )
})

const imageVariants:Variants = {
  initial: { opacity: 0, transition: { ...getTransition(), delay: 0 } },
  animate: { opacity: 1, transition: { ...getTransition(), duration: 1, delay: 0.3 } },
  exit: { opacity: 0, transition: { ...getTransition(), duration: 0.2, delay: 0 } }
}

const slideSizes = `(min-width: 768px) ${vw(520, 'desktop')}, ${vw(374, 'mobile')}`

const getSlide = (images: TypeCmsVariant['media'], idx: number) => {
  const image = images[idx]
  return image ? <Img media={image.medium} lazy={idx > 5} sizes={slideSizes} quality={99} /> : null
}

export const ImageSlider = memo<ImageSliderProps>(({ data }) => {
  const { currentSlide, triggerRef, nextSlide, prevSlide, setSlide, startAnimation } = useSlider(data.length)
  console.log('nextSlide, prevSlide', nextSlide, prevSlide)
  const currentImage = useMemo(() => getSlide(data, currentSlide), [data, currentSlide])

  useEffect(() => {
    startAnimation()
    setSlide(0)
  }, [data, setSlide, startAnimation])

  // console.log('ImageSlider data', data)

  if (!data?.length) return <SliderStyled />

  return (
    <SliderStyled ref={triggerRef} >
      <AnimatePresence mode='wait'>
        <motion.div key={currentSlide} variants={imageVariants} initial='initial' exit='exit' animate='animate'>
          {currentImage}
        </motion.div>
      </AnimatePresence>
    </SliderStyled>
  )
})

// {
//   data.length > 1 && (
//     <>
//       <button aria-label='Previous slide' onClick={prevSlide} className='control-btn prev' >
//         {/* eslint-disable-next-line @next/next/no-img-element */}
//         <img src='/images/svg/i--prevslide.svg' alt='' width='40' height='40' />
//       </button>
//       <button aria-label='Next slide' onClick={nextSlide} className='control-btn next' >
//         {/* eslint-disable-next-line @next/next/no-img-element */}
//         <img src='/images/svg/i--nextslide.svg' alt='' width='40' height='40' />
//       </button>
//       <Timeline currentIdx={currentSlide} nSlides={data.length} />
//     </>
//   )
// }

const SliderStyled = styled(motion.div)`
  align-items: center;
  display: flex;
  justify-content: center;
  padding: 40px 0 15px;
  min-height: ${vw(320, 'mobile')};
  position: relative;

  ${mq.greaterThan('nexus7')} {
    min-height: ${vw(320, 'tablet')};
  }

  ${mq.greaterThan('tablet')} {
    min-height: ${vw(360, 'desktop')};

    &:hover .control-btn {
      display: block;
    }
  }

  ${mq.greaterThan('desktop')} {
    min-height: 360px;
  }

  img {
    border-radius: 12px;
    height: 100%;
    object-fit: contain;
    overflow: hidden;
  }

  .control-btn {
    display: none;
    left: ${vw(20, 'mobile')};
    position: absolute;
    top: 50%;

    &.next {
      right: ${vw(20, 'mobile')};
      left: auto;
    }

    ${mq.greaterThan('nexus7')} {
      left: ${vw(20, 'tablet')};

      &.next {
        right: ${vw(20, 'tablet')};
      }
    }

    ${mq.greaterThan('tablet')} {
      left: ${vw(20, 'desktop')};
      top: calc(50% - ${vw(10, 'desktop')});

      &.next {
        right: ${vw(20, 'desktop')};
      }
    }

    ${mq.greaterThan('desktop')} {
      left: 20px;
      top: calc(50% - 10px);

      &.next {
        right: 20px;
      }
    }
  }
`

const TimelineContainer = styled.div`
  .span-container {
    background-color: ${rgbAlpha('--color-text', 0.2)};
    border-radius: 3px;
    display: block;
    height: ${vw(2, 'mobile')};
    position: relative;

    span {
      background-color: var(--color-text);
      border-radius: 3px;
      display: block;
      height: ${vw(2, 'mobile')};
      position: absolute;
    }
  }

  ${mq.greaterThan('tablet')} {
    margin: ${vw(10, 'desktop')} 0;

    .span-container {
      height: ${vw(3, 'desktop')};
      span { height: ${vw(3, 'desktop')}; }
    }
  }

  ${mq.greaterThan('desktop')} {
    margin: 10px 0;

    .span-container {
      height: 3px;
      span { height: 3px; }
    }
  }
`
